import ApiService from './api';
import { useDemoStore } from '@/stores/demo';
import type { DemoGoalId } from '@/components/demoV3/demoGoals';

/** Error envelope returned by demo endpoints when a request fails. */
export interface FallbackError {
  /** Always 'ERROR' — discriminator against successful payloads. */
  status: 'ERROR';
  /** Numeric error code from the API; 0 when unknown. */
  code: number;
  /** Human-readable description of the failure. */
  message: string;
}

/** A demo persona (e.g. Sales Representative) used to generate example questions. */
export interface DemoPersona {
  /** Arbitrary identifier to find what question is for which agent (e.g. `salesRepresentative`); also used in the UI translation file. */
  id: string;
  /** Display name of the persona */
  name: string;
  /** What this persona does / their role description */
  description: string;
}

/** Details of a website the funnel's AI Agent is for, as read by the API. */
export interface DemoWebsite {
  /** Normalized URL, with protocol */
  url: string;
  /** Site name, if the site declares one */
  name: string | null;
  /** The favicon, re-hosted on our own media server so the visitor's browser never requests the site itself */
  favicon: {
    original: string | null;
    hosted: string | null;
  } | null;
  /** The site's declared theme color, as a plain CSS color */
  themeColor: string | null;
}

/** Why a website can't be used for the funnel. */
export type DemoWebsiteErrorReason = 'invalid' | 'unreachable' | 'blocked';

/** A page of the visitor's website */
export interface DemoPage {
  url: string;
  title: string | null;
}

/** A question and its pre-generated answer, for the preview chat. */
export interface DemoPreviewChatItem {
  /** The question the visitor can ask */
  userQuery: string;
  /** The answer, as one or more bubbles */
  answer: { text: string }[];
  /** The pages the answer is based on */
  sources: DemoPage[];
}

/**
 * Where the AI Agent creation is at. The preview chat is generated in
 * `createQuestions` and `createAnswers`, and done once it is `ready`.
 */
export type DemoChatbotCreationStatus =
  | 'readSite'
  | 'extractBrand'
  | 'createQuestions'
  | 'createAnswers'
  | 'finalizing'
  | 'ready'
  | 'failed';

/** Demo creation flow v3: where a submitted demo is at, from the submit until the lead redeems their invite. */
export type DemoLeadStatus =
  /** Waiting for the lead to confirm their email with the code mailed to them. Nothing is built before. */
  | 'unconfirmed'
  | 'submitted'
  | 'building'
  | 'review'
  | 'released'
  | 'redeemed';

/** Demo creation flow v3, for admins: the review of a lead bot. Nothing redacted. */
export interface DemoLeadReview {
  /** The lead bot, `null` while it is still being created, or if that failed */
  bot: { uniqueBotId: string; stagingBotId: string | null; name: string | null } | null;
  website: { url: string | null; name: string | null };
  /** As the website calls the company, read from it by the API */
  companyName: string | null;
  purpose: DemoGoalId | null;
  contact: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    language: string | null;
  };
  /** What the lead agreed to on submit, when, and to which wording of the consent texts */
  consent: { marketing: boolean; legal: boolean; at: string | null; version: string | null } | null;
  /** Query parameters of the funnel's first page, e.g. UTM */
  queryParams: { key: string; value: string }[];
  leadStatus: DemoLeadStatus;
  submittedAt: string | null;
  /** When the bot is released automatically, unless held */
  readyAt: string | null;
  isPaused: boolean;
  /** Username of the admin who held it */
  heldBy: string | null;
  heldAt: string | null;
  releasedAt: string | null;
  /** `auto`, or the username of the admin who released it */
  releasedBy: string | null;
  invitedAt: string | null;
  /** What inviting the lead did, as told by the API */
  inviteResult: string | null;
  /** When the lead redeemed their invite, creating their Hub account */
  redeemedAt: string | null;
}

/**
 * Demo creation flow v3: a submitted demo in the admin list of demo requests,
 * next to the legacy rows. Has their basic shape, plus `version` and `lead`.
 */
export interface DemoLeadRequest {
  _id: string;
  version: 3;
  /** When it was submitted */
  createdAt: string;
  contact: { name: string; email: string | null; company: string | null; event?: string };
  /** `uniqueBotId` is `null` while the bot is still being created, or if that failed */
  bot: { uniqueBotId: string | null; name: string | null; createdAt: string | null };
  /** The same as the review banner of the lead bot gets */
  lead: DemoLeadReview;
}

/**
 * Demo creation flow v3: the kinds of work the funnel limits. Reading a website,
 * generating a preview chat, mailing a code that confirms the email, or checking one.
 */
export type DemoRateLimitKind = 'scrape' | 'generate' | 'code-mail' | 'code-guess';
/** What a rate limit counts per: one run through the funnel, or an IP across runs */
export type DemoRateLimitScope = 'session' | 'ip';

/** A limit of a kind of work, per session or per IP */
export interface DemoRateLimit {
  /** The limit the code sets */
  default: number;
  /** The limit in effect, of the config cached in Redis */
  limit: number;
}

/** The work counted against one session or IP in the current window */
export interface DemoRateLimitCounter {
  /** To reset it */
  key: string;
  kind: DemoRateLimitKind;
  scope: DemoRateLimitScope;
  /** The session's user ID, or the IP */
  id: string;
  count: number;
  /** In effect now, `null` for none */
  limit: number | null;
  /** When the window ends and the counter starts over */
  resetsAt: string | null;
}

/** Demo creation flow v3, for admins: the funnel's rate limits, live */
export interface DemoRateLimits {
  /** `null` for a scope the kind has no limit per */
  limits: { kind: DemoRateLimitKind; windowSeconds: number; session: DemoRateLimit | null; ip: DemoRateLimit | null }[];
  /**
   * When the config in Redis expires, and the limits go back to those of the
   * code. 12 h after it was cached or last changed.
   */
  configExpiresAt: string | null;
  /** The fullest first */
  counters: DemoRateLimitCounter[];
  /** Whether there were more counters than the API reads */
  truncated: boolean;
  /** The admin's own IP, as the API sees it. At an event, likely the venue's. */
  yourIp: string;
}

/** Demo creation flow v3: what the public status page shows. Contact details come redacted. */
export interface DemoPublicStatus {
  website: Pick<DemoWebsite, 'url' | 'name' | 'favicon'> | null;
  contact: {
    /** Masked, e.g. `m••••@example.com` */
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  submittedAt: string | null;
  /** How far the AI Agent got. A held one is still `review`. */
  leadStatus: DemoLeadStatus;
  /** When the AI Agent is released at the latest. `null` until the email is confirmed. */
  readyAt: string | null;
  /** While `unconfirmed`: when a new code can be mailed. May have passed already. */
  resendAvailableAt: string | null;
  /** While `unconfirmed`: whether the latest code can still confirm the email. If not, only a new code helps. */
  codeUsable: boolean | null;
}

/**
 * Demo creation flow v3: why a request around the code that confirms the email failed, if the API told us.
 * - `wrongCode`: `attemptsLeft` tells how many guesses the code takes still
 * - `codeExpired`, `tooManyAttempts`: only a new code helps
 * - `cooldown`: a code was mailed just now, `rateLimited`: too many requests. Both with `retryAfter`.
 * - `mailFailed`: the new code couldn't be mailed
 * - `invalidEmail`: the changed email isn't one
 */
export type DemoEmailCodeErrorReason =
  | 'wrongCode'
  | 'codeExpired'
  | 'tooManyAttempts'
  | 'cooldown'
  | 'rateLimited'
  | 'mailFailed'
  | 'invalidEmail';

/** A failed request of the demo creation flow v3, with what the API told us about why */
export type DemoReasonedError<Reason extends string> = FallbackError & {
  reason: Reason | null;
  /** Seconds to wait, when rate limited */
  retryAfter: number | null;
  /** Guesses the code takes still, for `wrongCode` */
  attemptsLeft: number | null;
};

/** Demo creation flow v3: what the API has on the visitor's AI Agent so far. */
export interface DemoChatbotStatus {
  /** Not set until a goal was picked */
  status?: DemoChatbotCreationStatus;
  /** The goal the preview chat was generated for */
  purpose?: DemoGoalId;
  website?: DemoWebsite;
  /** Name of the AI Agent, in the language of the website */
  agentName: string | null;
  /** First message of the AI Agent in the preview chat, in the language of the website */
  greeting: string | null;
  /** Empty until `status` is `ready` */
  previewChat: DemoPreviewChatItem[];
  /** The funnel is done: what follows is the status page, opened by its token */
  submitted: boolean;
}

/**
 * The API's error body (`{code, message, status, data?}`) of a failed request. Logged in, `ApiService`'s interceptor
 * throws the body itself. Without that (e.g. the funnel's visitors), the request throws axios' own error, which has
 * the body in `response.data`.
 */
const apiErrorBody = (error: any): any => error?.response?.data ?? error;

/** Like {@link fallbackError}, plus the details the API gives in `data` */
const reasonedError = <Reason extends string>(error: any): DemoReasonedError<Reason> => {
  const data = apiErrorBody(error)?.data;
  return {
    ...fallbackError(error),
    reason: data?.reason ?? null,
    retryAfter: typeof data?.retryAfter === 'number' ? data.retryAfter : null,
    attemptsLeft: typeof data?.attemptsLeft === 'number' ? data.attemptsLeft : null,
  };
};

/** Normalizes any thrown/returned error into the {@link FallbackError} shape. */
const fallbackError = (thrown: any): FallbackError => {
  console.log(thrown);

  const error = apiErrorBody(thrown);
  if (!error?.code || !error?.message) {
    return {
      status: 'ERROR',
      code: 0,
      message: 'An unknown error occurred',
    };
  }

  return {
    status: 'ERROR',
    code: error.code,
    message: error.message,
  };
}

const DemoService = {
  /** Returns the demo visitor's user ID from localStorage, falling back to the store's fingerprint ID. */
  getLocalUserId(): string | null {
    return localStorage.getItem('moin-user-id') || useDemoStore().fingerprintIdGetter;
  },
  setLocalUserId(userId: string): void {
    localStorage.setItem('moin-user-id', userId);
  },
  getUserIdWithFingerprintId: async ({ fingerprintId }: {
    /** Browser fingerprint used to identify the anonymous demo visitor */
    fingerprintId: string;
  }, queryParams: { key: string; value: string }[] = []): Promise<any> => {
    try {
      // Forward the funnel's URL query params (e.g. UTM) to the API
      const searchParams = new URLSearchParams();
      queryParams.forEach((p) => searchParams.append(p.key, p.value));
      const qs = searchParams.toString();
      const res = await ApiService.get(
        `/schaltzentrale/demo/init/${fingerprintId}${qs ? '?' + qs : ''}`
      );
      return res?.data;
    } catch (error) {
      console.error(error);
      return fallbackError(error);
    }
  },
  /**
   * @deprecated - No longer used (called by Demo#handleStepCompany, which is itself also deprecated)
   */
  async getQuestionsWithCompany({ site, fingerprintId, userId, language }: {
    /** Company website to derive questions from */
    site: string;
    /** Browser fingerprint used to identify the anonymous demo visitor */
    fingerprintId: string;
    /** Demo user ID (unused here; the local one is used instead) */
    userId?: string | null;
    /** Language for the generated questions */
    language?: string;
  }): Promise<any> {
    let res: any = null;
    try {
      if (!site) return false;

      const body: Record<string, any> = {
        companyDescription: {
          site,
        },
        language,
        userId: this.getLocalUserId() || null,
      };

      if (import.meta.env.MODE !== 'production') {
        body.noSlack = true;
      }

      res = await ApiService.post(
        `/schaltzentrale/demo/generateTopQueries/${fingerprintId}`,
        body
      );

      if (res.data.status === 'ERROR') {
        return { status: 'ERROR', message: res.data.message, queries: [] };
      }

      return res.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Generate the top questions a specific persona (e.g. Sales Representative) would get on the site.
   * Generates multiple questions for multiple personas at once.
   */
  async getQuestionsWithCompanyUsingPersonas({ site, userId, language, personas }: {
    /** Company website to derive questions from */
    site: string;
    /** Demo user ID; falls back to the locally stored one */
    userId?: string | null;
    /** Language for the generated questions, e.g. 'de' or 'en' */
    language: string;
    /** The personas to generate questions for */
    personas: DemoPersona[];
  }): Promise<FallbackError | { personas: Array<{ persona: DemoPersona; queries: { id: string; text: string }[] }> }> {
    try {
      const body: Record<string, any> = {
        companyDescription: {
          site,
        },
        language,
        userId: userId || this.getLocalUserId() || null,
        personas,
      };

      if (import.meta.env.MODE !== 'production') {
        body.noSlack = true;
      }

      const res = await ApiService.post(
        `/schaltzentrale/demo/v2/generateTopQueries/personas`,
        body
      );

      if (res.data.status === 'ERROR') {
        return fallbackError(res.data);
      }

      return res.data;
    } catch(error) {
      return fallbackError(error);
    }
  },
  /**
   * Detects which technologies (shop systems, CMS, ...) a website uses.
   * @returns `{status: 'OK', technologies}` on success, an error status object otherwise.
   */
  async analyzeSiteTechnologies({ url, userId }: {
    /** The website URL to analyze */
    url: string;
    /** Demo user ID; falls back to the locally stored one */
    userId?: string | null;
  }): Promise<FallbackError | { status: string; message: any } | { status: 'OK'; technologies: string[] }> {
    if (!userId) userId = this.getLocalUserId() || null;

    try {
      const res = await ApiService.post(
        `/schaltzentrale/demo/v2/analyze/technologies`,
        { url, userId },
      );

      if (res.status !== 200) {
        return { status: 'ERROR', message: res.data.message };
      }

      return res.data;
    } catch(error) {
      return fallbackError(error);
    }
  },
  /**
   * @deprecated - No longer used.
   */
  async getIntentsWithQuestions({ excludedQueries, fingerprintId, userId }: {
    /** Queries already shown that should be excluded from generation */
    excludedQueries: any;
    /** Browser fingerprint used to identify the anonymous demo visitor */
    fingerprintId: string;
    /** Demo user ID; falls back to the locally stored one */
    userId?: string | null;
  }): Promise<any> {
    try {
      const body: Record<string, any> = {
        excludedQueries,
        userId: userId || this.getLocalUserId() || null,
      };

      if (import.meta.env.MODE !== 'production') {
        body.noSlack = true;
      }

      const res = await ApiService.post(
        `/schaltzentrale/demo/generateTopIntents/${fingerprintId}`,
        body
      );
      return res.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  async getFeaturesWithQuestions({ excludedIds, personaIdentifier, userId, site, language = 'de' }: {
    /** Feature IDs already shown that should be excluded from generation */
    excludedIds: any;
    /** ID of the persona to generate features/questions for */
    personaIdentifier: string;
    /** Demo user ID; falls back to the locally stored one */
    userId?: string | null;
    /** Company website to derive features from */
    site: string;
    /** Language for the generated content (default 'de') */
    language?: string;
  }): Promise<any> {
    try {
      const body: Record<string, any> = {
        excludedIds,
        personaIdentifier,
        userId: userId || this.getLocalUserId() || null,
        language,
        site,
      };

      if (import.meta.env.MODE !== 'production') {
        body.noSlack = true;
      }

      const res = await ApiService.post(
        `/schaltzentrale/demo/v2/generateTopFeatures`,
        body
      );
      return res.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  async createDemoAccount({ contact, userId, language = 'de', site }: {
    /** Contact form data of the prospect (mutated: sendToHubspot is forced off outside production) */
    contact: Record<string, any>;
    /** Demo user ID; falls back to the locally stored one */
    userId?: string | null;
    /** Language of the prospect (default 'de') */
    language?: string;
    /** The website the demo was created for */
    site: string;
  }): Promise<any> {
    try {
      if (import.meta.env.MODE !== 'production') {
        contact.sendToHubspot = false;
      }

      const body: Record<string, any> = {
        contact: {
          ...contact,
          language,
        },
        userId: userId || this.getLocalUserId() || null,
        site,
      };

      if (import.meta.env.MODE !== 'production') {
        body.noSlack = true;
      }

      const res = await ApiService.post(
        `/schaltzentrale/demo/v2/submit`,
        body
      );

      return res.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  async getDemoRequests({ dateRange = null }: {
    /** Optional [since, to] pair of moment-like objects with a .format() method */
    dateRange?: any[] | null;
  }): Promise<any> {
    let dates = '';

    if (dateRange) {
      dates = `?since=${dateRange[0].format(
        'YYYY-MM-DD'
      )}&to=${dateRange[1].format('YYYY-MM-DD')}`;
    }

    try {
      const res = await ApiService.get('/schaltzentrale/demo-hub' + dates);
      return res.data;
    } catch (error) {
      return { error };
    }
  },
  async addCommentToDemoRequest(botId: string, { comment }: {
    /** The internal comment text to attach to the demo request */
    comment: string;
  }): Promise<any> {
    try {
      const response = await ApiService.post(
        `/schaltzentrale/demo-hub/comment/${botId}`,
        {
          comment,
        }
      );
      return response?.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  async deleteDemoRequest(demoId: string): Promise<any> {
    try {
      const response = await ApiService.delete(
        `/schaltzentrale/demo-hub/${demoId}`
      );
      return response?.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Generates a chatbot introduction message given a persona, and some company information that is stored in the DB on this user.
   * @param data Request payload; `data.userId` is filled from local storage when missing, and
   * `noSlack` is forced on outside production (the object is mutated).
   */
  async createAgentIntroduction(data: {
    /** Temporary user ID created for the visitor, or the current logged in user */
    userId?: string | null;
    /** Language to use */
    language: string;
    /** The title of the agent, e.g. "Customer Success Manager" */
    agentTitle: string;
    /** What the agent does. Either the default prompt or a customized one if user edited it */
    agentDescription: string;
    /** The website the user is currently doing a demo for */
    site: string;
    /** Suppress the Slack notification (set automatically outside production) */
    noSlack?: boolean;
  }): Promise<FallbackError | { introduction: string | null; status: 'OK' | 'ERROR' }> {
    if (!data.userId) data.userId = this.getLocalUserId() || null;

    if (import.meta.env.MODE !== 'production') {
      data.noSlack = true;
    }

    try {
      const response = await ApiService.post(
        `/schaltzentrale/demo/v2/generateAgentIntroduction`,
        data,
      );
      return response?.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3: sets the website the AI Agent is for, and reads its details.
   * @returns The website details, or an error with the `reason` why the site can't be used,
   * if the API told us.
   */
  async setWebsite({ userId, url }: {
    /** The demo session, from the init request */
    userId: string;
    /** The domain or URL as entered */
    url: string;
  }): Promise<{ status: 'OK'; website: DemoWebsite } | (FallbackError & { reason: DemoWebsiteErrorReason | null })> {
    try {
      const response = await ApiService.post('/schaltzentrale/demo/v3/website', { userId, url });
      return response.data;
    } catch (error: any) {
      return {
        ...fallbackError(error),
        reason: apiErrorBody(error)?.data?.reason ?? null,
      };
    }
  },
  /**
   * Demo creation flow v3: sets the goal of the AI Agent. The API starts
   * generating the preview chat for it, poll {@link getChatbotStatus} for the result.
   */
  async setChatbotPurpose({ userId, purpose }: {
    /** The demo session, from the init request */
    userId: string;
    purpose: DemoGoalId;
  }): Promise<{ status: 'OK' } | FallbackError> {
    try {
      const response = await ApiService.post('/schaltzentrale/demo/v3/purpose', { userId, purpose });
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3: the final submit. Saves the contact, mails the code
   * that confirms the email and returns the token for the status page. Nothing
   * is built before the code is confirmed. No Slack notifications and no
   * HubSpot contact outside production.
   */
  async submitDemo({ userId, contact, consent }: {
    /** The demo session, from the init request */
    userId: string;
    contact: { firstName: string; lastName: string; email: string; language: string };
    /** `version` identifies the wording of the consent texts the visitor saw */
    consent: { marketing: boolean; legal: boolean; version: string };
  }): Promise<{ status: 'OK'; token: string } | DemoReasonedError<'rateLimited'>> {
    const isTest = import.meta.env.MODE !== 'production';
    try {
      const response = await ApiService.post('/schaltzentrale/demo/v3/submit', {
        userId,
        contact,
        consent,
        noSlack: isTest,
        noHubspot: isTest,
      });
      return response.data;
    } catch (error) {
      return reasonedError(error);
    }
  },
  /** Demo creation flow v3, for admins: the review of a lead bot. */
  async getLeadReview(botId: string): Promise<{ status: 'OK'; lead: DemoLeadReview } | FallbackError> {
    try {
      const response = await ApiService.get(`/schaltzentrale/demo-hub/lead/${encodeURIComponent(botId)}`);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3, for admins: adds the admin themselves to a lead bot,
   * to review it. Only works while the bot is in the `lead` stage.
   * `changed` is `false` if they had the bot already.
   */
  async joinLeadBot(botId: string): Promise<{ status: 'OK'; changed: boolean } | FallbackError> {
    try {
      const response = await ApiService.post(`/schaltzentrale/demo-hub/lead/${encodeURIComponent(botId)}/join`);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3, for admins: `release` invites the lead right away
   * (also if held), `hold` stops the release at `readyAt`, `unhold` resumes it.
   * `changed` is `false` if there was nothing to do, e.g. it was released already.
   */
  async updateLeadReview(botId: string, action: 'release' | 'hold' | 'unhold'): Promise<{ status: 'OK'; changed: boolean; lead: DemoLeadReview } | FallbackError> {
    try {
      const response = await ApiService.post(`/schaltzentrale/demo-hub/lead/${encodeURIComponent(botId)}/${action}`);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /** Demo creation flow v3, for admins: the funnel's rate limits and their counters, live. */
  async getRateLimits(): Promise<({ status: 'OK' } & DemoRateLimits) | FallbackError> {
    try {
      const response = await ApiService.get('/schaltzentrale/demo-hub/rate-limits');
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3, for admins: resets one counter by its `key`, or all
   * of them, so their sessions or IPs can go on right away.
   */
  async resetRateLimits(what: { key: string } | { all: true }): Promise<{ status: 'OK'; reset: number } | FallbackError> {
    try {
      const response = await ApiService.post('/schaltzentrale/demo-hub/rate-limits/reset', what);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3, for admins: changes a limit in the config in Redis,
   * until it expires 12 h later.
   */
  async setRateLimit(kind: DemoRateLimitKind, scope: DemoRateLimitScope, limit: number): Promise<{ status: 'OK' } | FallbackError> {
    try {
      const response = await ApiService.put(`/schaltzentrale/demo-hub/rate-limits/config/${kind}/${scope}`, { limit });
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /** Demo creation flow v3, for admins: all limits back to those of the code. */
  async resetRateLimitConfig(): Promise<{ status: 'OK' } | FallbackError> {
    try {
      const response = await ApiService.delete('/schaltzentrale/demo-hub/rate-limits/config');
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /** Demo creation flow v3: what the public status page shows, by its token. */
  async getChatbotStatusByToken(token: string): Promise<{ status: 'OK'; chatbot: DemoPublicStatus } | FallbackError> {
    try {
      const response = await ApiService.get(`/schaltzentrale/demo/v3/status/token/${encodeURIComponent(token)}`);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
  /**
   * Demo creation flow v3: confirms the lead's email with the code from the mail,
   * which starts building the AI Agent. Answers with the status, as {@link getChatbotStatusByToken}.
   */
  async confirmLeadEmail(token: string, code: string): Promise<{ status: 'OK'; chatbot: DemoPublicStatus } | DemoReasonedError<DemoEmailCodeErrorReason>> {
    try {
      const response = await ApiService.post(`/schaltzentrale/demo/v3/status/token/${encodeURIComponent(token)}/confirm`, { code });
      return response.data;
    } catch (error) {
      return reasonedError(error);
    }
  },
  /**
   * Demo creation flow v3: mails the lead a new code, replacing the one before.
   * Answers with the status, as {@link getChatbotStatusByToken}.
   */
  async resendLeadCode(token: string): Promise<{ status: 'OK'; chatbot: DemoPublicStatus } | DemoReasonedError<DemoEmailCodeErrorReason>> {
    try {
      const response = await ApiService.post(`/schaltzentrale/demo/v3/status/token/${encodeURIComponent(token)}/resend`, {});
      return response.data;
    } catch (error) {
      return reasonedError(error);
    }
  },
  /**
   * Demo creation flow v3: changes the lead's email while it's unconfirmed, and
   * mails a new code there. Answers with the status, as {@link getChatbotStatusByToken}.
   */
  async changeLeadEmail(token: string, email: string): Promise<{ status: 'OK'; chatbot: DemoPublicStatus } | DemoReasonedError<DemoEmailCodeErrorReason>> {
    try {
      const response = await ApiService.post(`/schaltzentrale/demo/v3/status/token/${encodeURIComponent(token)}/email`, { email });
      return response.data;
    } catch (error) {
      return reasonedError(error);
    }
  },
  /** Demo creation flow v3: where the AI Agent creation is at, including the preview chat once it's ready. */
  async getChatbotStatus(userId: string): Promise<{ status: 'OK'; chatbot: DemoChatbotStatus } | FallbackError> {
    try {
      const response = await ApiService.get(`/schaltzentrale/demo/v3/status/${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error) {
      return fallbackError(error);
    }
  },
};

export default DemoService;
